import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../auth/session';
import { useSolMemories } from '../../data/queries/use-sol-memories';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  clearSolMemories,
  describeMemoryWriteError,
  forgetSolMemory,
  replaceSolMemory,
} from '../../data/writers/sol-memory-writers';
import type { SolMemoryRow } from '../../data/database.types';
import { formatInstant } from '../../lib/timezone';
import { GLOBAL_SOL_PATH } from './sol-routes';
import {
  MEMORY_CLEARED_TEXT,
  MEMORY_FORGOTTEN_TEXT,
  MEMORY_KIND_LABEL,
  MEMORY_ORIGIN_LABEL,
  memoryClearPromptText,
} from './sol-wording';
import './sol.css';

/**
 * WHAT SOL REMEMBERS — `/sol/memory`. M11 / D-040.
 *
 * **EVERY MEMORY, VISIBLE AND UNDER HIS CONTROL.** Each row shows the words,
 * the kind, and — plainly — whether he asked Sol to remember it or Sol noted
 * it from something he said. Forget removes one; Edit corrects one (the old
 * row is superseded, never silently rewritten); Clear all forgets everything,
 * behind a confirmation.
 *
 * **NOTHING HERE IS OPERATIONAL.** The three writers reach `sol_memory` and
 * nothing else; no Capture, Project, status or report is in their path, and
 * the operational Confirm card is not involved — these are his own notes
 * about how Sol should work, not saved site information.
 *
 * **NO AUTO-MEMORY TOGGLE, DELIBERATELY.** D-040 makes automatic memory ON
 * and narrow — preference and instruction, from his own words only, always
 * visible, always one tap from Forget. That visibility and that tap are the
 * control; a switch that silently turns noting off would be a second setting
 * to explain and nothing a person asked for.
 */

type RowState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'editing'; readonly draft: string }
  | { readonly kind: 'busy' }
  | { readonly kind: 'failed'; readonly text: string };

export function SolMemoryScreen() {
  const { profile } = useSession();
  const timeZone = profile?.timezone ?? null;
  const { status, memories, error, reload } = useSolMemories();
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [clearOffered, setClearOffered] = useState(false);
  const [clearing, setClearing] = useState(false);
  const inFlight = useRef(false);

  const setRow = (id: string, state: RowState): void => {
    setRowStates((previous) => ({ ...previous, [id]: state }));
  };
  const rowState = (id: string): RowState => rowStates[id] ?? { kind: 'idle' };

  const forget = (memory: SolMemoryRow): void => {
    const client = getSupabaseClient();
    if (!client || inFlight.current) return;
    inFlight.current = true;
    setRow(memory.id, { kind: 'busy' });
    setNotice(null);
    void forgetSolMemory(client, memory.id)
      .then(() => {
        setNotice(MEMORY_FORGOTTEN_TEXT);
        setRow(memory.id, { kind: 'idle' });
        return reload();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setRow(memory.id, { kind: 'failed', text: describeMemoryWriteError(message) });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  const saveEdit = (memory: SolMemoryRow, draft: string): void => {
    const client = getSupabaseClient();
    const content = draft.trim().replace(/\s+/g, ' ');
    if (!client || inFlight.current) return;
    if (content.length === 0 || content.length > 500) {
      setRow(memory.id, { kind: 'failed', text: 'A memory must be between 1 and 500 characters.' });
      return;
    }
    if (content === memory.content) {
      setRow(memory.id, { kind: 'idle' });
      return;
    }
    inFlight.current = true;
    setRow(memory.id, { kind: 'busy' });
    setNotice(null);
    void replaceSolMemory(client, memory.id, memory.kind, content)
      .then(() => {
        setNotice('Sol updated what it remembers.');
        setRow(memory.id, { kind: 'idle' });
        return reload();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setRow(memory.id, { kind: 'failed', text: describeMemoryWriteError(message) });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  const clearAll = (): void => {
    const client = getSupabaseClient();
    if (!client || inFlight.current) return;
    inFlight.current = true;
    setClearing(true);
    setNotice(null);
    void clearSolMemories(client)
      .then(() => {
        setNotice(MEMORY_CLEARED_TEXT);
        setClearOffered(false);
        return reload();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setNotice(describeMemoryWriteError(message));
      })
      .finally(() => {
        inFlight.current = false;
        setClearing(false);
      });
  };

  return (
    <main className="cl-screen sol-screen">
      <p className="project-home-back">
        <Link className="cl-link" to={GLOBAL_SOL_PATH}>
          ‹ Sol
        </Link>
      </p>

      <section className="sol" aria-labelledby="sol-memory-heading">
        <header className="sol-header">
          <h1 id="sol-memory-heading" className="cl-screen-title">
            Sol memory
          </h1>
          <p className="sol-context">
            Preferences, standing instructions and business context Sol keeps for you. It never
            stores Capture or Project facts here — those live in Crewlink and are always read fresh.
          </p>
        </header>

        {notice ? (
          <p className="sol-history-notice" role="status">
            {notice}
          </p>
        ) : null}

        {status === 'loading' ? (
          <p className="sol-pending" role="status">
            Loading what Sol remembers…
          </p>
        ) : null}
        {status === 'failed' ? (
          <p className="sol-problem" role="alert">
            {error}
          </p>
        ) : null}
        {status === 'ready' && memories.length === 0 ? (
          <p className="sol-empty">
            Sol doesn&rsquo;t remember anything yet. Tell it &ldquo;remember I like client reports
            short&rdquo; and it will appear here.
          </p>
        ) : null}

        {memories.length > 0 ? (
          <ul className="sol-memory-list">
            {memories.map((memory) => {
              const state = rowState(memory.id);
              const when = timeZone
                ? formatInstant(memory.updated_at, timeZone, { dateStyle: 'medium' })
                : '';
              return (
                <li key={memory.id} className="sol-memory-item">
                  <p className="sol-memory-kind">
                    {MEMORY_KIND_LABEL[memory.kind]}
                    {when ? ` · ${when}` : ''}
                  </p>
                  {state.kind === 'editing' ? (
                    <div className="sol-memory-edit">
                      <label
                        className="sol-composer-label"
                        htmlFor={`sol-memory-edit-${memory.id}`}
                      >
                        Edit memory
                      </label>
                      <textarea
                        id={`sol-memory-edit-${memory.id}`}
                        className="sol-composer-input"
                        rows={2}
                        value={state.draft}
                        onChange={(event) =>
                          setRow(memory.id, { kind: 'editing', draft: event.target.value })
                        }
                      />
                      <div className="sol-history-actions">
                        <button
                          type="button"
                          className="cl-button"
                          onClick={() => saveEdit(memory, state.draft)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="sol-start-over"
                          onClick={() => setRow(memory.id, { kind: 'idle' })}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="sol-memory-content">{memory.content}</p>
                  )}
                  <p className="sol-memory-origin">{MEMORY_ORIGIN_LABEL[memory.origin]}</p>
                  {state.kind === 'failed' ? (
                    <p className="sol-problem" role="alert">
                      {state.text}
                    </p>
                  ) : null}
                  {state.kind === 'editing' ? null : (
                    <div className="sol-history-actions">
                      <button
                        type="button"
                        className="sol-start-over"
                        onClick={() =>
                          setRow(memory.id, { kind: 'editing', draft: memory.content })
                        }
                        disabled={state.kind === 'busy'}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="sol-start-over sol-history-delete"
                        onClick={() => forget(memory)}
                        disabled={state.kind === 'busy'}
                      >
                        {state.kind === 'busy' ? 'Working…' : 'Forget'}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}

        {status === 'ready' && memories.length > 0 ? (
          clearOffered ? (
            <div className="sol-action" role="group" aria-label="Clear all memories">
              <p className="sol-action-prompt">{memoryClearPromptText(memories.length)}</p>
              <div className="sol-action-buttons">
                <button
                  type="button"
                  className="sol-action-confirm sol-action-confirm--danger"
                  onClick={clearAll}
                  disabled={clearing}
                >
                  {clearing ? 'Clearing…' : 'Clear all memories'}
                </button>
                <button
                  type="button"
                  className="sol-action-cancel"
                  onClick={() => setClearOffered(false)}
                  disabled={clearing}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="sol-visit-controls">
              <button
                type="button"
                className="sol-start-over sol-history-delete"
                onClick={() => setClearOffered(true)}
              >
                Clear all memories
              </button>
            </p>
          )
        ) : null}
      </section>
    </main>
  );
}
